import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  FolderOpen,
  Warehouse,
  Calculator,
  ClipboardList,
  Users,
  BarChart3,
  Settings,
  LogOut,
  ShoppingBag
} from 'lucide-react';
import { Button } from './ui/button';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'supervisor', 'cajero'] },
  { path: '/pos', icon: ShoppingCart, label: 'POS / Ventas', roles: ['admin', 'supervisor', 'cajero'] },
  { path: '/products', icon: Package, label: 'Productos', roles: ['admin', 'supervisor'] },
  { path: '/categories', icon: FolderOpen, label: 'Categorías', roles: ['admin', 'supervisor'] },
  { path: '/inventory', icon: Warehouse, label: 'Inventario', roles: ['admin', 'supervisor'] },
  { path: '/cash', icon: Calculator, label: 'Caja', roles: ['admin', 'supervisor', 'cajero'] },
  { path: '/cash-sessions', icon: ClipboardList, label: 'Arqueos', roles: ['admin', 'supervisor'] },
  { path: '/users', icon: Users, label: 'Usuarios', roles: ['admin'] },
  { path: '/reports', icon: BarChart3, label: 'Reportes', roles: ['admin', 'supervisor'] },
  { path: '/settings', icon: Settings, label: 'Configuración', roles: ['admin'] },
];

export default function Sidebar({ isOpen, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filteredItems = navItems.filter(item => item.roles.includes(user?.role || ''));

  return (
    <aside
      className={`fixed top-0 left-0 z-40 h-screen w-64 bg-white border-r border-stone-200 transition-transform lg:translate-x-0 ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
      data-testid="sidebar"
    >
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="flex items-center gap-3 p-6 border-b border-stone-100">
          <div className="w-10 h-10 rounded-xl bg-amber-600 flex items-center justify-center">
            <ShoppingBag className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-stone-900 font-heading">Sweet Sales</h1>
            <p className="text-xs text-stone-500">Sistema POS</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {filteredItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onClose}
              data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-amber-50 text-amber-700 font-medium'
                    : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900'
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User info & logout */}
        <div className="p-4 border-t border-stone-100">
          <div className="flex items-center gap-3 px-4 py-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center">
              <span className="text-sm font-semibold text-stone-600">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-stone-900 truncate">{user?.name}</p>
              <p className="text-xs text-stone-500 capitalize">{user?.role}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full justify-start gap-3 text-stone-600 hover:text-red-600 hover:bg-red-50 rounded-xl"
          >
            <LogOut className="w-5 h-5" />
            Cerrar sesión
          </Button>
        </div>
      </div>
    </aside>
  );
}
